import { Box, Typography } from '@mui/material'
import { splitCompactNumber } from '~/util/numbers'

export type DataValueSize = 'md' | 'sm' | 'lg'

const VARIANT = { md: 'dataValue', sm: 'caption', lg: 'dataValueLg' } as const
/** Unit suffix scale per size — the unit steps back so the NUMBER is what's loud. */
const UNIT_SX = {
  md: { fontSize: '0.8em', fontWeight: 400 },
  sm: { fontSize: '0.85em', fontWeight: 400 },
  lg: { fontSize: '0.55em', fontWeight: 500 },
} as const

interface Props {
  value: number
  /** Σ / ø prefix; rendered muted. Not offered at lg (hero numbers carry no glyph). */
  glyph?: 'Σ' | 'ø'
  size?: DataValueSize
}

/** Wave-4 §2 (D3): one renderer for every compact data value — muted glyph, loud
 *  number, dimmed smaller unit. `sm` rides caption size in text.secondary (the ø
 *  slot); `md`/`lg` ride the dataValue/dataValueLg theme variants.
 *  D5: a negative `sm` value steps UP to text.primary + semibold — negative numbers
 *  are never allowed to read as quieter than positive ones. `md`/`lg` are already
 *  text.primary + bold by variant, so they carry no separate negative treatment. */
export function DataValue({ value, glyph, size = 'md' }: Props) {
  const { number, unit } = splitCompactNumber(value)
  const isNegSm = size === 'sm' && value < 0
  return (
    <Typography
      variant={VARIANT[size]}
      component="span"
      noWrap
      data-testid="data-value"
      sx={{
        color: size === 'sm' ? (value < 0 ? 'text.primary' : 'text.secondary') : 'text.primary',
        ...(size === 'sm' ? { fontVariantNumeric: 'tabular-nums' } : {}),
        ...(isNegSm ? { fontWeight: 600 } : {}),
      }}
    >
      {glyph ? (
        <Box
          component="span"
          data-testid="data-value-glyph"
          sx={{ color: 'text.secondary', fontWeight: 400 }}
        >
          {glyph}{' '}
        </Box>
      ) : null}
      {number}
      {unit ? (
        <Box
          component="span"
          data-testid="data-value-unit"
          sx={{ color: 'text.secondary', ...UNIT_SX[size] }}
        >
          {' '}
          {unit}
        </Box>
      ) : null}
    </Typography>
  )
}
